// src/pages/SaasApplicationsFormPage.js
// Rescoped for SaaS Applications specifically — multiple Access Owners and
// Billing Owners instead of a single owner, plus a Security section. See
// chat for the field list this was rescoped against.
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PeoplePicker from "../components/PeoplePicker";
import { SystemImageUpload } from "../components/SystemImage";
import { saasApplicationsApi as api } from "../utils/api";
import { loadConfig } from "../utils/config";

const TWO_FACTOR_OPTIONS = ["Enforced", "Allowed", "Turned Off", "Not Available", "Unknown"];
const CENTRAL_MGMT_OPTIONS = ["Yes", "Partial", "No"];
const AUTH_TYPE_OPTIONS = ["Entra ID", "Individual"];

const empty = {
  name: "", description: "", category: "", url: "",
  accessOwners: [], billingOwners: [],
  auditIntervalMonths: 12,
  twoFactor: "Unknown", passwordRequirements: "", passwordExpiry: "",
  centralManagement: "No", securityNotes: "",
  authType: "", securityGroup: "", authNotes: "",
  accessLevels: [],
  imageVersion: 0,
};

function calcNextAuditDue(system, months) {
  const base = system?.lastAuditDate ? new Date(system.lastAuditDate)
    : system?.createdAt ? new Date(system.createdAt) : new Date();
  const next = new Date(base);
  next.setMonth(next.getMonth() + months);
  return next.toISOString();
}

function AccessLevelEditor({ levels, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const val = input.trim();
    if (!val || levels.includes(val)) return;
    onChange([...levels, val]);
    setInput("");
  };
  const remove = (l) => onChange(levels.filter(x => x !== l));
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10, minHeight: 26 }}>
        {levels.map(l => (
          <div key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "var(--purple-light)", borderRadius: 99, padding: "3px 10px" }}>
            <span style={{ fontSize: 12, color: "var(--purple-dark)", fontWeight: 500 }}>{l}</span>
            <button type="button" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--purple-dark)", fontSize: 12, padding: 0, lineHeight: 1 }} onClick={() => remove(l)}>✕</button>
          </div>
        ))}
        {levels.length === 0 && <span style={{ fontSize: 12, color: "var(--gray-400)" }}>Using global default access levels</span>}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="e.g. Admin, Viewer…" style={{ flex: 1 }} />
        <button type="button" className="btn-secondary" onClick={add}>Add</button>
      </div>
    </div>
  );
}

// Multi-person picker — search adds to a list of chips, each removable.
// Reuses PeoplePicker for the search box; remounts it after each pick
// (via `key`) so its internal query/value resets ready for the next search.
function MultiPeoplePicker({ people, onChange, placeholder }) {
  const [pickerKey, setPickerKey] = useState(0);

  const addPerson = (person) => {
    if (!person) return;
    if (people.some(p => p.upn === person.upn)) { setPickerKey(k => k + 1); return; }
    onChange([...people, { upn: person.upn, displayName: person.displayName, email: person.email }]);
    setPickerKey(k => k + 1);
  };
  const removePerson = (upn) => onChange(people.filter(p => p.upn !== upn));

  return (
    <div>
      <PeoplePicker key={pickerKey} value={null} onChange={addPerson} placeholder={placeholder} ownerOnly={true} />
      {people.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {people.map(p => (
            <div key={p.upn} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--blue-light)", borderRadius: 99, padding: "4px 10px" }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>{p.displayName}</span>
              <button type="button" onClick={() => removePerson(p.upn)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, padding: 0, lineHeight: 1 }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// A field, one per row — label above, help text below if given.
function Field({ label, required, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>
        {label}{required && <span style={{ color: "var(--red)", marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: 12, color: "var(--gray-500)", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

// A titled card grouping related fields — the main visual unit of the page.
function FormCard({ title, description, adminOnly, children }) {
  return (
    <div className="card" style={{ marginBottom: 16, padding: "20px 24px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: "var(--gray-800)" }}>{title}</h2>
        {adminOnly && <span className="badge badge-neutral" style={{ fontSize: 10 }}>Admins only</span>}
      </div>
      {description && <p style={{ fontSize: 13, color: "var(--gray-500)", marginBottom: 16 }}>{description}</p>}
      {children}
    </div>
  );
}

const AUTH_TYPES = [
  { value: "none",   label: "None" },
  { value: "header", label: "API key in a header" },
  { value: "bearer", label: "Bearer token" },
  { value: "basic",  label: "Basic auth (username:password)" },
  { value: "query",  label: "API key in the URL (query param)" },
];

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

const EMPTY_SYNC_CONFIG = {
  enabled: false,
  sourceType: "api",
  groups: [], groupPrefix: "", groupExcludes: "", groupPrefixMatches: 0,
  url: "", method: "GET",
  queryParams: [], body: "",
  authType: "none", authFieldName: "", hasSecret: false,
  headers: [],
  activePath: "", activeValues: "",
  usersPath: "", upnPath: "", displayNamePath: "", usernamePath: "", emailPath: "", accessLevelPath: "",
  deactivateMissing: false, keyExpiryDate: "",
  lastRunAt: "", lastStatus: "never", lastError: "", lastSummary: "",
};

// A row of the Params / Headers key-value editors below.
function KeyValueRows({ rows, onChange, keyPlaceholder, valuePlaceholder }) {
  const update = (i, field, val) => {
    const next = rows.slice();
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { key: "", value: "" }]);

  return (
    <div>
      {rows.map((row, i) => (
        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
          <input value={row.key} onChange={e => update(i, "key", e.target.value)} placeholder={keyPlaceholder} style={{ fontSize: 13, flex: 1 }} />
          <input value={row.value} onChange={e => update(i, "value", e.target.value)} placeholder={valuePlaceholder} style={{ fontSize: 13, flex: 1 }} />
          <button type="button" className="btn-ghost" style={{ fontSize: 13, color: "var(--red)" }} onClick={() => remove(i)}>✕</button>
        </div>
      ))}
      <button type="button" className="btn-ghost" style={{ fontSize: 12, paddingLeft: 0 }} onClick={add}>+ Add row</button>
    </div>
  );
}

// A labeled sub-section within the API sync card — just a heading + gap,
// so the request-builder sections (URL/Params/Body/Auth/Headers/Filter/
// Data Mapping) read as clearly distinct blocks.
function SyncSubSection({ title, hint, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--gray-700)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.03em" }}>{title}</h4>
      {hint && <p style={{ fontSize: 11, color: "var(--gray-500)", marginBottom: 8 }}>{hint}</p>}
      {children}
    </div>
  );
}

// Automatically pulling in user access from a third-party API — shaped like
// a standard HTTP request builder (URL / Params / Body / Auth / Headers),
// plus Filter and Data Mapping for turning the response into user records.
// Separate save flow from the main system form (its own endpoint, and the
// secret specifically never round-trips through the browser once saved).
function ApiSyncSection({ systemId }) {
  const [cfg, setCfg] = useState(EMPTY_SYNC_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const [secretInput, setSecretInput] = useState("");
  const [editingSecret, setEditingSecret] = useState(false);
  const [savingSecret, setSavingSecret] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const [showHistory, setShowHistory] = useState(false);
  const [runs, setRuns] = useState(null);
  const [runsLoading, setRunsLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedRunLoading, setSelectedRunLoading] = useState(false);

  const loadRuns = () => {
    setShowHistory(s => !s);
    if (runs === null) {
      setRunsLoading(true);
      fetch(`/api/saas-applications/${systemId}/api-sync/runs`)
        .then(r => r.json())
        .then(data => setRuns(Array.isArray(data) ? data : []))
        .catch(() => setRuns([]))
        .finally(() => setRunsLoading(false));
    }
  };

  const openRun = (runId) => {
    setSelectedRun(null);
    setSelectedRunLoading(true);
    fetch(`/api/saas-applications/${systemId}/api-sync/runs/${runId}`)
      .then(r => r.json())
      .then(setSelectedRun)
      .catch(() => setSelectedRun({ error: "Could not load this run." }))
      .finally(() => setSelectedRunLoading(false));
  };

  useEffect(() => {
    fetch(`/api/saas-applications/${systemId}/api-sync`)
      .then(r => r.json())
      .then(data => setCfg({ ...EMPTY_SYNC_CONFIG, ...data }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [systemId]);

  const set = (k, v) => { setCfg(c => ({ ...c, [k]: v })); setSaved(false); };

  const handleSave = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const res = await fetch(`/api/saas-applications/${systemId}/api-sync`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(cfg),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save");
      setCfg({ ...EMPTY_SYNC_CONFIG, ...data });
      setSaved(true);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleSaveSecret = async () => {
    if (!secretInput.trim()) { setError("Enter a key/token first, or Cancel."); return; }
    setSavingSecret(true); setError("");
    try {
      const res = await fetch(`/api/saas-applications/${systemId}/api-sync/secret`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ authValue: secretInput }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to save key");
      setCfg(c => ({ ...c, hasSecret: true }));
      setSecretInput(""); setEditingSecret(false);
    } catch (err) { setError(err.message); }
    finally { setSavingSecret(false); }
  };

  const handleClearSecret = async () => {
    if (!window.confirm("Remove the stored API key/token? Sync will fail until a new one is set.")) return;
    try {
      await fetch(`/api/saas-applications/${systemId}/api-sync/secret`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ clear: true }),
      });
      setCfg(c => ({ ...c, hasSecret: false }));
    } catch (err) { setError(err.message); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null); setSyncResult(null); setError("");
    try {
      const res = await fetch(`/api/saas-applications/${systemId}/api-sync/test`, { method: "POST" });
      const data = await res.json();
      setTestResult(data);
      // Preview only — deliberately does not touch cfg.lastRunAt/lastStatus,
      // since a dry run isn't a real sync.
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    } finally { setTesting(false); }
  };

  const handleSyncNow = async () => {
    if (!window.confirm("This will actually add, update, or deactivate users based on the API's current response. Continue?")) return;
    setSyncing(true); setSyncResult(null); setTestResult(null); setError("");
    try {
      const res = await fetch(`/api/saas-applications/${systemId}/api-sync/sync`, { method: "POST" });
      const data = await res.json();
      setSyncResult(data);
      setCfg(c => ({ ...c, lastRunAt: new Date().toISOString(), lastStatus: data.ok ? "ok" : "error", lastError: data.ok ? "" : data.error, lastSummary: data.summary || "" }));
    } catch (err) {
      setSyncResult({ ok: false, error: err.message });
    } finally { setSyncing(false); }
  };

  if (loading) return (
    <div className="card" style={{ marginBottom: 16, padding: "18px 20px" }}>
      <span className="spinner" style={{ width: 16, height: 16 }} />
    </div>
  );

  const showAuthField = cfg.authType === "header" || cfg.authType === "query";
  const bodyMethods = ["POST", "PUT", "PATCH"];
  const keyExpired = cfg.keyExpiryDate && new Date(cfg.keyExpiryDate).getTime() < Date.now();
  const keyExpiringSoon = cfg.keyExpiryDate && !keyExpired && new Date(cfg.keyExpiryDate).getTime() < Date.now() + 14 * 24 * 60 * 60 * 1000;

  return (
    <div className="card" style={{ marginBottom: 16, padding: "18px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700 }}>User sync</h3>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, cursor: "pointer", margin: 0 }}>
          <input type="checkbox" checked={cfg.enabled} onChange={e => set("enabled", e.target.checked)} style={{ width: "auto" }} />
          Enabled
        </label>
      </div>

      <SyncSubSection title="Source">
        <select value={cfg.sourceType} onChange={e => set("sourceType", e.target.value)} style={{ fontSize: 13, maxWidth: 280 }}>
          <option value="api">Custom API</option>
          <option value="group">Entra Security Group</option>
        </select>
      </SyncSubSection>

      {cfg.sourceType === "group" ? (
        <SyncSubSection title="Security Groups" hint="Membership itself is the access list — anyone in any included group is active, anyone removed from all of them gets deactivated automatically. Someone in several groups gets all of those roles.">
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Group name prefix</label>
            <input value={cfg.groupPrefix} onChange={e => set("groupPrefix", e.target.value)} placeholder="e.g. APP-Foo-" style={{ fontSize: 13, maxWidth: 320 }} />
            <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 4 }}>
              Every Entra group whose name starts with this is included, and its role is the rest of the name — <code>APP-Foo-Finance-Approver</code> gives the role <code>Finance-Approver</code>. New groups with the prefix are picked up automatically.
            </div>
            {cfg.groupPrefix && cfg.groupPrefixMatches > 0 && (
              <div style={{ marginTop: 6 }}><span className="badge badge-ok" style={{ fontSize: 11 }}>Matched {cfg.groupPrefixMatches} group{cfg.groupPrefixMatches !== 1 ? "s" : ""} when last saved</span></div>
            )}
          </div>
          {cfg.groupPrefix && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Exclude groups (optional)</label>
              <textarea value={cfg.groupExcludes} onChange={e => set("groupExcludes", e.target.value)} rows={3}
                placeholder={"One per line — a group's full name or Object ID"} style={{ width: "100%", fontSize: 12, resize: "vertical" }} />
            </div>
          )}
          <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Specific groups (optional)</label>
          <div style={{ fontSize: 11, color: "var(--gray-400)", marginBottom: 8 }}>
            Add a group by Object ID to include one that doesn't match the prefix, or to give a prefix-matched group a different role than its name suggests.
          </div>
          {(cfg.groups || []).map((g, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" }}>
              <div style={{ flex: 2 }}>
                <input value={g.groupId} onChange={e => {
                  const next = [...cfg.groups]; next[i] = { ...next[i], groupId: e.target.value }; set("groups", next);
                }} placeholder="Group Object ID (GUID)" style={{ fontSize: 13 }} />
                {g.groupName && (
                  <div style={{ marginTop: 4 }}>
                    <span className="badge badge-ok" style={{ fontSize: 11 }}>{g.groupName}</span>
                  </div>
                )}
              </div>
              <input value={g.accessLevel} onChange={e => {
                const next = [...cfg.groups]; next[i] = { ...next[i], accessLevel: e.target.value }; set("groups", next);
              }} placeholder="Access level (e.g. Admin)" style={{ fontSize: 13, flex: 1 }} />
              <button type="button" className="btn-ghost" style={{ fontSize: 13, color: "var(--red)" }}
                onClick={() => set("groups", cfg.groups.filter((_, idx) => idx !== i))}>✕</button>
            </div>
          ))}
          <button type="button" className="btn-ghost" style={{ fontSize: 12, paddingLeft: 0 }}
            onClick={() => set("groups", [...(cfg.groups || []), { groupId: "", groupName: "", accessLevel: "Member" }])}>
            + Add group
          </button>
          <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 8 }}>
            Find a group's Object ID on its page in Entra ID → Overview. Names are resolved automatically when you save.
          </div>
        </SyncSubSection>
      ) : (
        <>
      <p style={{ fontSize: 12, color: "var(--gray-500)", marginBottom: 16 }}>
        Build the request exactly like you would in Postman: URL, Params, Body, Auth, Headers — then tell it how to read the response.
      </p>

      <SyncSubSection title="URL">
        <div style={{ display: "flex", gap: 8 }}>
          <select value={cfg.method} onChange={e => set("method", e.target.value)} style={{ fontSize: 13, width: 100, flexShrink: 0 }}>
            {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <input value={cfg.url} onChange={e => set("url", e.target.value)} placeholder="https://api.example.com/v1/users" style={{ fontSize: 13 }} />
        </div>
      </SyncSubSection>

      <SyncSubSection title="Params" hint="Query-string parameters appended to the URL above.">
        <KeyValueRows rows={cfg.queryParams} onChange={v => set("queryParams", v)} keyPlaceholder="page" valuePlaceholder="1" />
      </SyncSubSection>

      {bodyMethods.includes(cfg.method) && (
        <SyncSubSection title="Body" hint="Raw JSON, sent as-is with the request.">
          <textarea value={cfg.body} onChange={e => set("body", e.target.value)} rows={3}
            placeholder='{"active_only": true}' style={{ width: "100%", fontSize: 12, fontFamily: "monospace", resize: "vertical" }} />
        </SyncSubSection>
      )}

      <SyncSubSection title="Auth" hint="The one section with a real secret — stored separately and never shown again once saved.">
        <div style={{ display: "grid", gridTemplateColumns: showAuthField ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Type</label>
            <select value={cfg.authType} onChange={e => set("authType", e.target.value)} style={{ fontSize: 13 }}>
              {AUTH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {showAuthField && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>
                {cfg.authType === "query" ? "Query parameter name" : "Header name"}
              </label>
              <input value={cfg.authFieldName} onChange={e => set("authFieldName", e.target.value)} placeholder={cfg.authType === "query" ? "api_key" : "X-API-Key"} style={{ fontSize: 13 }} />
            </div>
          )}
        </div>

        {cfg.authType !== "none" && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>
              {cfg.authType === "basic" ? "Username:password" : cfg.authType === "bearer" ? "Bearer token" : "API key / token"}
            </label>
            {cfg.hasSecret && !editingSecret ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="badge badge-ok">Key is set</span>
                <button type="button" className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setEditingSecret(true)}>Change</button>
                <button type="button" className="btn-ghost" style={{ fontSize: 12, color: "var(--red)" }} onClick={handleClearSecret}>Remove</button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8 }}>
                <input type="password" value={secretInput} onChange={e => setSecretInput(e.target.value)} placeholder="Paste key/token…" style={{ fontSize: 13 }} />
                <button type="button" className="btn-secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }} disabled={savingSecret} onClick={handleSaveSecret}>
                  {savingSecret ? <span className="spinner" style={{ width: 12, height: 12 }} /> : "Save key"}
                </button>
                {cfg.hasSecret && (
                  <button type="button" className="btn-ghost" style={{ fontSize: 12 }} onClick={() => { setEditingSecret(false); setSecretInput(""); }}>Cancel</button>
                )}
              </div>
            )}
          </div>
        )}
      </SyncSubSection>

      <SyncSubSection title="Headers" hint="Any additional custom headers this API needs, beyond Auth above.">
        <KeyValueRows rows={cfg.headers} onChange={v => set("headers", v)} keyPlaceholder="X-Tenant-Id" valuePlaceholder="12345" />
      </SyncSubSection>

      <SyncSubSection title="Filter" hint="Which returned records count as active/in-scope.">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Active/status path</label>
            <input value={cfg.activePath} onChange={e => set("activePath", e.target.value)} placeholder="status" style={{ fontSize: 13 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Values meaning active</label>
            <input value={cfg.activeValues} onChange={e => set("activeValues", e.target.value)} placeholder="active, enabled" style={{ fontSize: 13 }} />
          </div>
        </div>
        <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 4 }}>Comma-separated. Leave blank to treat any truthy value as active.</div>
      </SyncSubSection>

      <SyncSubSection title="Data Mapping" hint="Where the user list is in the response, and which field is which.">
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Path to the user list</label>
          <input value={cfg.usersPath} onChange={e => set("usersPath", e.target.value)} placeholder="data.users" style={{ fontSize: 13 }} />
          <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 4 }}>Leave blank if the response is already just an array.</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>UPN / email path</label>
            <input value={cfg.upnPath} onChange={e => set("upnPath", e.target.value)} placeholder="email" style={{ fontSize: 13 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Display name path</label>
            <input value={cfg.displayNamePath} onChange={e => set("displayNamePath", e.target.value)} placeholder="full_name  or  {{first_name}} {{last_name}}" style={{ fontSize: 13 }} />
            <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 4 }}>Combine multiple fields with <code>{"{{field}}"}</code>, e.g. <code>{"{{first_name}} {{last_name}}"}</code>. Only used for people who don't already have an Entra ID or Custom User account — recognized people always show their real name.</div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Username path</label>
            <input value={cfg.usernamePath} onChange={e => set("usernamePath", e.target.value)} placeholder="username" style={{ fontSize: 13 }} />
            <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 4 }}>Optional — a separate login handle, if this system has one distinct from email/UPN.</div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Email path</label>
            <input value={cfg.emailPath} onChange={e => set("emailPath", e.target.value)} placeholder="email" style={{ fontSize: 13 }} />
            <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 4 }}>If different from the UPN field above.</div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>Access level / role path</label>
            <input value={cfg.accessLevelPath} onChange={e => set("accessLevelPath", e.target.value)} placeholder="role" style={{ fontSize: 13 }} />
          </div>
        </div>
      </SyncSubSection>
        </>
      )}

      <SyncSubSection title="Automation">
        {cfg.sourceType === "group" ? (
          <div style={{ fontSize: 12, color: "var(--gray-500)", marginBottom: 10 }}>
            Anyone removed from the group is automatically deactivated here — always on for group sync, since Entra's membership list is always complete.
          </div>
        ) : (
          <>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", marginBottom: 10 }}>
              <input type="checkbox" checked={cfg.deactivateMissing} onChange={e => set("deactivateMissing", e.target.checked)} style={{ width: "auto" }} />
              Deactivate users this sync previously added if they stop appearing in the API
            </label>
            <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: -6, marginBottom: 14, marginLeft: 20 }}>
              Off by default — only turn this on if you're confident the API always returns its full, unpaginated user list.
            </div>
            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>API key/token expiry date (optional)</label>
            <input type="date" value={cfg.keyExpiryDate} onChange={e => set("keyExpiryDate", e.target.value)} style={{ fontSize: 13, maxWidth: 180 }} />
            <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 4 }}>Just a reminder you set — nothing here can verify the actual key.</div>
          </>
        )}
      </SyncSubSection>

      {cfg.sourceType !== "group" && (keyExpired || keyExpiringSoon) && (
        <div style={{ fontSize: 12, color: keyExpired ? "var(--red)" : "#7A3C00", background: keyExpired ? "var(--red-light)" : "var(--orange-light)", borderRadius: "var(--radius)", padding: "8px 12px", marginBottom: 14 }}>
          {keyExpired ? "This key has expired." : "This key expires within 14 days."}
        </div>
      )}

      {error && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 10 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <button type="button" className="btn-primary" style={{ fontSize: 13 }} disabled={saving} onClick={handleSave}>
          {saving ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Saving…</> : "Save API sync settings"}
        </button>
        <button type="button" className="btn-secondary" style={{ fontSize: 13 }} disabled={testing} onClick={handleTest}>
          {testing ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Testing…</> : "Test connection now"}
        </button>
        <button type="button" className="btn-secondary" style={{ fontSize: 13, color: "var(--red)" }} disabled={syncing} onClick={handleSyncNow}>
          {syncing ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Syncing…</> : "Sync now"}
        </button>
        {saved && <span style={{ fontSize: 12, color: "var(--green)", alignSelf: "center" }}>✓ Saved</span>}
      </div>
      <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: -10, marginBottom: 14 }}>
        <strong>Test connection now</strong> calls the API and shows what it returns — it never changes any user's access.{" "}
        <strong>Sync now</strong> actually applies those changes immediately, the same as the nightly run would.
      </div>

      {testResult && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, padding: "10px 12px", borderRadius: "var(--radius)", marginBottom: testResult.ok ? 8 : 0, background: testResult.ok ? "var(--purple-light)" : "var(--red-light)", color: testResult.ok ? "var(--purple-dark)" : "var(--red)" }}>
            {testResult.ok ? `Preview — ${testResult.summary}` : `✕ ${testResult.error}`}
          </div>
          {testResult.ok && testResult.groups && testResult.groups.length > 0 && (
            <details style={{ marginBottom: 8, fontSize: 12 }}>
              <summary style={{ cursor: "pointer", color: "var(--purple)", fontWeight: 500 }}>
                {testResult.groups.length} group{testResult.groups.length !== 1 ? "s" : ""} matched — view groups and roles
              </summary>
              <div style={{ maxHeight: 240, overflowY: "auto", marginTop: 6 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "var(--gray-50)" }}>
                      {["Group", "Role", "Members"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontSize: 11, color: "var(--gray-500)", fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {testResult.groups.map((g, i) => (
                      <tr key={i} style={{ background: i % 2 ? "var(--gray-50)" : "transparent" }}>
                        <td style={{ padding: "6px 10px" }}>{g.groupName}</td>
                        <td style={{ padding: "6px 10px", fontWeight: 500 }}>{g.accessLevel}</td>
                        <td style={{ padding: "6px 10px", color: "var(--gray-500)" }}>{g.memberCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
          {testResult.ok && testResult.preview && testResult.preview.length > 0 && (
            <div style={{ border: "none", borderRadius: "var(--radius)", overflow: "hidden", maxHeight: 320, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "var(--gray-50)" }}>
                    {["Name", "UPN / Email", "Username", "Access Level", "Active", "Would"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontSize: 11, color: "var(--gray-500)", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {testResult.preview.map((u, i) => (
                    <tr key={i} style={{ background: i % 2 ? "var(--gray-50)" : "transparent" }}>
                      <td style={{ padding: "6px 10px" }}>
                        {u.displayName || "—"}
                        {!u.recognized && <span className="badge badge-neutral" style={{ fontSize: 10, marginLeft: 6 }}>Unrecognized</span>}
                      </td>
                      <td style={{ padding: "6px 10px", color: "var(--gray-500)" }}>{u.upn}</td>
                      <td style={{ padding: "6px 10px", color: "var(--gray-500)" }}>{u.username || "—"}</td>
                      <td style={{ padding: "6px 10px" }}>{String(u.accessLevel ?? "—")}</td>
                      <td style={{ padding: "6px 10px" }}>{u.active ? "Yes" : "No"}</td>
                      <td style={{ padding: "6px 10px", fontWeight: 500, color: (u.action === "no change" || u.action?.startsWith("ignored")) ? "var(--gray-400)" : u.action === "add" ? "var(--green)" : (u.action === "update" || u.action === "reactivate") ? "var(--purple)" : "var(--red)" }}>
                        {u.action}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {syncResult && (
        <div style={{ fontSize: 12, padding: "10px 12px", borderRadius: "var(--radius)", marginBottom: 12, background: syncResult.ok ? "var(--green-light)" : "var(--red-light)", color: syncResult.ok ? "var(--green)" : "var(--red)" }}>
          {syncResult.ok ? `✓ Synced — ${syncResult.summary}` : `✕ ${syncResult.error}`}
        </div>
      )}

      <div style={{ fontSize: 12, color: "var(--gray-500)" }}>
        Last run: {cfg.lastStatus === "never" ? "Never run yet" : (
          <>
            {new Date(cfg.lastRunAt).toLocaleString("en-GB")} —{" "}
            <span style={{ color: cfg.lastStatus === "ok" ? "var(--green)" : "var(--red)", fontWeight: 500 }}>
              {cfg.lastStatus === "ok" ? "Succeeded" : "Failed"}
            </span>
            {cfg.lastStatus === "ok" && cfg.lastSummary && ` — ${cfg.lastSummary}`}
            {cfg.lastStatus === "error" && cfg.lastError && ` — ${cfg.lastError}`}
          </>
        )}
      </div>

      <button type="button" className="btn-ghost" style={{ fontSize: 12, paddingLeft: 0, marginTop: 8 }} onClick={loadRuns}>
        {showHistory ? "Hide sync history" : "View sync history"}
      </button>

      {showHistory && (
        <div style={{ marginTop: 8 }}>
          {runsLoading ? (
            <span className="spinner" style={{ width: 14, height: 14 }} />
          ) : !runs || runs.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--gray-400)" }}>No sync runs recorded yet — "Test connection now" doesn't count, since it never changes anything.</div>
          ) : (
            <div style={{ border: "none", borderRadius: "var(--radius)", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "var(--gray-50)" }}>
                    {["When", "Trigger", "Status", "Summary", ""].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontSize: 11, color: "var(--gray-500)", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r, i) => (
                    <tr key={r.id} style={{ background: i % 2 ? "var(--gray-50)" : "transparent" }}>
                      <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>{new Date(r.timestamp).toLocaleString("en-GB")}</td>
                      <td style={{ padding: "6px 10px", color: "var(--gray-500)" }}>{r.triggeredBy === "nightly" ? "Nightly" : "Manual"}</td>
                      <td style={{ padding: "6px 10px" }}>
                        <span style={{ color: r.status === "ok" ? "var(--green)" : "var(--red)", fontWeight: 500 }}>{r.status === "ok" ? "Succeeded" : "Failed"}</span>
                      </td>
                      <td style={{ padding: "6px 10px", color: "var(--gray-500)" }}>{r.status === "ok" ? r.summary : r.error}</td>
                      <td style={{ padding: "6px 10px" }}>
                        {r.status === "ok" && <button type="button" className="btn-ghost" style={{ fontSize: 11 }} onClick={() => openRun(r.id)}>View detail →</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {(selectedRunLoading || selectedRun) && (
        <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && setSelectedRun(null)}>
          <div className="modal" style={{ maxWidth: 720 }}>
            <div className="modal-header">
              <h2>Sync run detail</h2>
              <button className="btn-ghost" onClick={() => setSelectedRun(null)}>✕</button>
            </div>
            <div className="modal-body">
              {selectedRunLoading ? (
                <span className="spinner" style={{ width: 16, height: 16 }} />
              ) : selectedRun.error && !selectedRun.details ? (
                <div style={{ fontSize: 13, color: "var(--red)" }}>{selectedRun.error}</div>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: "var(--gray-500)", marginBottom: 4 }}>
                    {new Date(selectedRun.timestamp).toLocaleString("en-GB")} — {selectedRun.triggeredBy === "nightly" ? "Nightly run" : "Manual run"} — {selectedRun.summary}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--gray-400)", marginBottom: 12 }}>
                    Only shows people who were actually added, updated, deactivated, or reactivated — not everyone the API returned. Use "Test connection now" to see the full list, including anyone ignored or unchanged.
                  </div>
                  {selectedRun.truncated && (
                    <div style={{ fontSize: 11, color: "#7A3C00", background: "var(--orange-light)", borderRadius: "var(--radius)", padding: "6px 10px", marginBottom: 12 }}>
                      This run affected more people than could be stored in full — showing a partial list. The counts in the summary above are still accurate.
                    </div>
                  )}
                  <div style={{ maxHeight: 420, overflowY: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: "var(--gray-50)" }}>
                          {["Name", "UPN / Email", "Username", "Access Level", "Active", "Action"].map(h => (
                            <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontSize: 11, color: "var(--gray-500)", fontWeight: 600, position: "sticky", top: 0, background: "var(--gray-50)" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedRun.details || []).map((u, i) => (
                          <tr key={i} style={{ background: i % 2 ? "var(--gray-50)" : "transparent" }}>
                            <td style={{ padding: "6px 10px" }}>
                              {u.displayName || "—"}
                              {!u.recognized && <span className="badge badge-neutral" style={{ fontSize: 10, marginLeft: 6 }}>Unrecognized</span>}
                            </td>
                            <td style={{ padding: "6px 10px", color: "var(--gray-500)" }}>{u.upn}</td>
                            <td style={{ padding: "6px 10px", color: "var(--gray-500)" }}>{u.username || "—"}</td>
                            <td style={{ padding: "6px 10px" }}>{String(u.accessLevel ?? "—")}</td>
                            <td style={{ padding: "6px 10px" }}>{u.active ? "Yes" : "No"}</td>
                            <td style={{ padding: "6px 10px", fontWeight: 500, color: (u.action === "no change" || u.action?.startsWith("ignored")) ? "var(--gray-400)" : u.action === "add" ? "var(--green)" : (u.action === "update" || u.action === "reactivate") ? "var(--purple)" : "var(--red)" }}>
                              {u.action}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SaasApplicationsFormPage({ isAdmin = true }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const backTo = isAdmin ? (isEdit ? `/saas-applications/${id}` : "/saas-applications") : `/owner-saas-application/${id}`;

  const [original, setOriginal] = useState(null); // the loaded item, for edit mode
  const [form,   setForm]   = useState(empty);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => { loadConfig().then(setConfig); }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const sys = await api.get(id);
        setOriginal(sys);
        setForm({ ...empty, ...sys });
      } catch {
        navigate(backTo);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]); // run once per :id param change

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleIntervalChange = (val) => {
    const nextDue = calcNextAuditDue(isEdit ? original : null, Number(val));
    setForm(f => ({ ...f, auditIntervalMonths: Number(val), nextAuditDue: nextDue }));
  };

  const handleImageUploaded = (newVersion) => {
    setForm(f => ({ ...f, imageVersion: newVersion }));
    if (original) setOriginal(o => ({ ...o, imageVersion: newVersion }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim())     { setError("SaaS Application name is required"); return; }
    if (!form.category.trim()) { setError("Category is required"); return; }
    if ((form.accessOwners || []).length === 0) { setError("At least one Access Owner is required"); return; }
    setSaving(true); setError("");
    try {
      const payload = {
        ...form,
        nextAuditDue: form.nextAuditDue || calcNextAuditDue(isEdit ? original : null, form.auditIntervalMonths),
      };
      const saved = isEdit ? await api.update(id, payload) : await api.create(payload);
      navigate(isAdmin ? `/saas-applications/${saved.id}` : `/owner-saas-application/${saved.id}`);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${form.name}"? This will also remove all its users. This cannot be undone.`)) return;
    setDeleting(true); setError("");
    try {
      await api.remove(id);
      navigate("/saas-applications");
    } catch (err) { setError(err.message); setDeleting(false); }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 80 }}><span className="spinner" /></div>;

  const previewDate = new Date(calcNextAuditDue(isEdit ? original : null, form.auditIntervalMonths))
    .toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  const categories     = config?.categories     || [];
  const auditIntervals = config?.auditIntervals || [1, 2, 3, 6, 12];

  const col2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 };

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto" }}>
      {/* Header: back link, title, and the primary actions — always visible, no scrolling needed to save */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <div>
          <button type="button" className="btn-ghost" style={{ paddingLeft: 0, color: "var(--purple)", marginBottom: 6 }} onClick={() => navigate(backTo)}>
            ← {isEdit ? "Back to SaaS Application" : "Back to SaaS Applications"}
          </button>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>
            {isEdit ? `Edit SaaS Application${form.name ? `: ${form.name}` : ""}` : "Add New SaaS Application"}
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {isAdmin && isEdit && (
            <button type="button" className="btn-ghost" style={{ color: "var(--red)" }} onClick={handleDelete} disabled={deleting || saving}>
              {deleting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Deleting…</> : "Delete"}
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={() => navigate(backTo)}>Cancel</button>
          <button type="submit" form="saas-application-form" className="btn-primary" disabled={saving}>
            {saving
              ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving…</>
              : (isEdit ? "Save Changes" : "Add SaaS Application")}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: "var(--red-light)", color: "var(--red)", padding: "10px 14px", borderRadius: "var(--radius)", marginBottom: 16, fontSize: 13 }}>{error}</div>
      )}

      {isEdit && isAdmin && (
        <div style={{ background: "var(--orange-light)", color: "#7A3C00", padding: "10px 14px", borderRadius: "var(--radius)", marginBottom: 16, fontSize: 13 }}>
          System owners can see everything on this page except Admin Notes. Use Admin Notes for anything you don't want them to see.
        </div>
      )}

      <form id="saas-application-form" onSubmit={handleSubmit}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 20, alignItems: "start" }}>

          {/* ── Main column ── */}
          <div>
            <FormCard title="SaaS Application Details" description="What this application is and how it's categorised.">
              <Field label="Name" required>
                <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Salesforce, GitHub" />
              </Field>
              <Field label="Description" hint="A short note on what this application is used for.">
                <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={3}
                  placeholder="What is this application used for?" style={{ width: "100%", resize: "vertical" }} />
              </Field>
              <div style={col2}>
                <Field label="Category" required>
                  <select value={form.category} onChange={e => set("category", e.target.value)}>
                    <option value="">Select category…</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="URL">
                  <input value={form.url} onChange={e => set("url", e.target.value)} placeholder="https://app.example.com" />
                </Field>
              </div>
            </FormCard>

            <FormCard title="Security" description="How well protected this application is, and how it's managed.">
              <div style={col2}>
                <Field label="2FA">
                  <select value={form.twoFactor} onChange={e => set("twoFactor", e.target.value)}>
                    {TWO_FACTOR_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Central Management">
                  <select value={form.centralManagement} onChange={e => set("centralManagement", e.target.value)}>
                    {CENTRAL_MGMT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
              <div style={col2}>
                <Field label="Password Requirements">
                  <input value={form.passwordRequirements} onChange={e => set("passwordRequirements", e.target.value)} placeholder="e.g. 12+ characters, complexity enforced" />
                </Field>
                <Field label="Password Expiry">
                  <input value={form.passwordExpiry} onChange={e => set("passwordExpiry", e.target.value)} placeholder="e.g. 90 days, Never" />
                </Field>
              </div>
              <Field label="Notes">
                <input value={form.securityNotes} onChange={e => set("securityNotes", e.target.value)} placeholder="Any other security-relevant notes…" />
              </Field>
            </FormCard>

            <FormCard title="Ownership & Audit Schedule" description="Who's responsible for this application, and how often access gets reviewed.">
              <Field label="Access Owner" required hint="Determines who sees this under 'SaaS Applications I Own'. Supports multiple people.">
                <MultiPeoplePicker people={form.accessOwners || []} onChange={val => set("accessOwners", val)} placeholder="Search Azure AD…" />
              </Field>
              <Field label="Billing Owner" hint="Informational only — doesn't affect 'SaaS Applications I Own'.">
                <MultiPeoplePicker people={form.billingOwners || []} onChange={val => set("billingOwners", val)} placeholder="Search Azure AD…" />
              </Field>
              <Field label="Audit Every" required hint={<>Next audit will fall due <strong>{previewDate}</strong></>}>
                <select value={form.auditIntervalMonths} onChange={e => handleIntervalChange(e.target.value)}>
                  {auditIntervals.map(i => <option key={i} value={i}>{i} {i === 1 ? "month" : "months"}</option>)}
                </select>
              </Field>
            </FormCard>

            <FormCard title="Authentication" description="How people sign in, and anything worth noting about it.">
              <div style={col2}>
                <Field label="Type">
                  <select value={form.authType} onChange={e => set("authType", e.target.value)}>
                    <option value="">Select type…</option>
                    {AUTH_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Security Group">
                  <input value={form.securityGroup} onChange={e => set("securityGroup", e.target.value)} placeholder="e.g. SG-Salesforce-Users" />
                </Field>
              </div>
              <Field label="Notes">
                <textarea value={form.authNotes} onChange={e => set("authNotes", e.target.value)} rows={3}
                  placeholder="e.g. SSO configured via Entra, break-glass account details…" style={{ width: "100%", resize: "vertical" }} />
              </Field>
            </FormCard>

            {isAdmin && (
              <FormCard title="Access Levels" adminOnly description="The roles/permissions available when assigning users to this application.">
                <AccessLevelEditor levels={form.accessLevels || []} onChange={val => set("accessLevels", val)} />
              </FormCard>
            )}

            {isAdmin && isEdit && <ApiSyncSection systemId={id} />}
          </div>

          {/* ── Sidebar: image + live preview, sticky so it stays visible while scrolling the form ── */}
          <div style={{ position: "sticky", top: 20 }}>
            {isAdmin && isEdit && (
              <div className="card" style={{ marginBottom: 16, padding: "18px 20px" }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: "var(--gray-700)" }}>SaaS Application Icon</h3>
                <SystemImageUpload
              resource="saas-applications"
                  systemId={id}
                  systemName={form.name}
                  imageVersion={form.imageVersion}
                  onUploaded={handleImageUploaded}
                />
              </div>
            )}
            {isAdmin && !isEdit && (
              <div className="card" style={{ marginBottom: 16, padding: "18px 20px" }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: "var(--gray-700)" }}>SaaS Application Icon</h3>
                <p style={{ fontSize: 12, color: "var(--gray-500)" }}>Save the application first, then come back here to add an icon.</p>
              </div>
            )}

            <div className="card" style={{ padding: "18px 20px" }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: "var(--gray-700)" }}>Preview</h3>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, wordBreak: "break-word" }}>{form.name || <span style={{ color: "var(--gray-400)" }}>Untitled application</span>}</div>
              {form.category && <span className="badge badge-info" style={{ fontSize: 11, marginBottom: 8, display: "inline-block" }}>{form.category}</span>}
              <div style={{ fontSize: 12, color: "var(--gray-500)", marginTop: 10, lineHeight: 1.7 }}>
                <div>👤 {(form.accessOwners||[]).length ? form.accessOwners.map(o=>o.displayName).join(", ") : <em>No access owner set</em>}</div>
                {(form.billingOwners||[]).length > 0 && <div>💳 {form.billingOwners.map(o=>o.displayName).join(", ")}</div>}
                <div>🔁 Reviewed every {form.auditIntervalMonths} month{form.auditIntervalMonths !== 1 ? "s" : ""}</div>
                <div>📅 Next review: {previewDate}</div>
                <div>🔐 2FA: {form.twoFactor}</div>
              </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
}
